import type { FunctionComponent } from 'preact';
import { useCallback } from 'preact/hooks';

import type { RepeaterFunctionInvoker, TemplateContextMap, TemplateFunction } from 'Components/types';
import { useResizeState } from 'Core/hooks/index';
import type { ServerModel } from 'Modules/serverApi/types';
import { cloneSafe } from 'Utils/components';

enum layoutType {
  horizontal = 'horizontal',
  vertical = 'vertical',
}

interface ItemParams {
  fields: string[];
}

export interface Params {
  items: RepeaterFunctionInvoker<ItemParams>;
  template: keyof typeof layoutType;
}

interface Props {
  template: TemplateFunction<Params>;
  fields: string[];
  data: Record<string, string>[] | ServerModel.GenericItem[];
  extraProps?: TemplateContextMap;
  columnBreakpoint?: number;
}

const Table: FunctionComponent<Props> = ({
  template,
  fields,
  data,
  extraProps = {},
  columnBreakpoint = 425,
}) => {
  const layout = useResizeState(
    useCallback(
      () => (window.innerWidth > columnBreakpoint ? layoutType.horizontal : layoutType.vertical),
      [columnBreakpoint],
    ),
  ) as keyof typeof layoutType;

  if (!data?.length) {
    return null;
  }

  const tableTitleData = Object.fromEntries(fields.map((field) => [field, field]));

  const items = [
    (templ) => cloneSafe(templ.call({ ...tableTitleData, fields }), null, { key: 'title' }),
    ...data.map((row) => (templ: TemplateFunction<ItemParams>) => {
      const key = Object.entries(row)
        .map((pair) => `${pair[0]}|${pair[1]}`)
        .join('&');
      const component = templ.call({ ...row, fields });
      return cloneSafe(component, null, { key });
    }),
  ] as RepeaterFunctionInvoker<ItemParams>;

  return template.call({ items, template: layout, ...extraProps });
};

export default Table;
